import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import Keycloak, { KeycloakConfig } from 'keycloak-js';
import { Config } from '../utils/envConfig';
import useTenant from '../hooks/useTenant';

type KeycloakProviderContextType = {
  keycloak: Keycloak | undefined;
};

const initialState: KeycloakProviderContextType = {
  keycloak: undefined
};

const KeycloakContext = createContext(initialState);

export const TOKEN_EXPIRATION = parseInt(Config.tokenExpiration || '0');

export const KeycloakProvider = ({ children }: PropsWithChildren) => {
  const { realm } = useTenant();
  const [keycloak, setKeycloak] = useState<Keycloak | undefined>();

  const keycloakInit = useCallback(async (currentRealm: string) => {
    const initOptions: KeycloakConfig = {
      url: Config.authUrl ?? '',
      realm: currentRealm,
      clientId: Config.authClientId ?? ''
    };

    const keycloakInstance = new Keycloak(initOptions);

    keycloakInstance.onTokenExpired = () => {
      keycloakInstance && keycloakInstance.updateToken(TOKEN_EXPIRATION);
    };

    try {
      const authenticated = await keycloakInstance.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        redirectUri: Config.clientUrl + document.location.pathname
      });
      if (authenticated) {
        setKeycloak(keycloakInstance);
      }
    } catch (error) {
      //TODO: report errors somewhere
    }
  }, []);

  useEffect(() => {
    if (realm) {
      keycloakInit(realm);
    }
  }, [realm, keycloakInit]);

  const value = { keycloak };

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = (): KeycloakProviderContextType => {
  const keycloakContext = useContext(KeycloakContext);
  if (!keycloakContext)
    throw new Error(
      'No KeycloakContext.Provider found when calling useKeycloakContext.'
    );
  return keycloakContext;
};
